/**
 * Auto-switch to first available color when size is selected
 * 
 * When a customer selects a size (Option1), this script automatically
 * switches the color selector (Option2) to the first in-stock color.
 * This prevents the "Everything is unavailable" moment and improves UX.
 */

(function() {
  'use strict';

  // Debug mode - set to true to enable console logging
  const DEBUG_MODE = false;

  /**
   * Log debug messages
   */
  function debugLog(...args) {
    if (DEBUG_MODE) {
      console.log('[Auto-Switch Variant Color]', ...args);
    }
  }

  /**
   * Get product variants JSON data
   */
  function getProductVariants() {
    const variantJsonElement = document.querySelector('[data-variant-json]');
    if (!variantJsonElement) {
      debugLog('⚠️ Variant JSON element not found');
      return null;
    }

    try {
      const variants = JSON.parse(variantJsonElement.textContent);
      debugLog('✅ Loaded variants:', variants.length);
      return variants;
    } catch (e) {
      debugLog('❌ Error parsing variant JSON:', e);
      return null;
    }
  }

  /**
   * Get currently selected option values
   * Returns an object with option indices and also identifies which is size and which is color
   */
  function getSelectedOptions() {
    const selectedOptions = {};
    let sizeOptionIndex = null;
    let colorOptionIndex = null;
    
    // Get all option inputs (selects and buttons)
    const optionWrappers = document.querySelectorAll('[dynamic-variant-input-wrap]');
    
    optionWrappers.forEach(wrapper => {
      const index = wrapper.dataset.index; // e.g., "option1", "option2"
      
      // Identify if this is size or color option
      const label = wrapper.querySelector('label');
      if (label) {
        const labelText = label.textContent.toLowerCase();
        if ((labelText.includes('size') && !labelText.includes('colour') && !labelText.includes('color')) && !sizeOptionIndex) {
          sizeOptionIndex = index;
        } else if ((labelText.includes('colour') || labelText.includes('color')) && !colorOptionIndex) {
          colorOptionIndex = index;
        }
      }
      
      // Try to get from select dropdown
      const select = wrapper.querySelector('select[dynamic-variant-input]');
      if (select && select.value) {
        selectedOptions[index] = select.value;
        return;
      }
      
      // Try to get from button (swatch/chip)
      const selectedButton = wrapper.querySelector('[dynamic-variant-button].selected, [dynamic-variant-button][aria-selected="true"]');
      if (selectedButton) {
        const optionValue = selectedButton.dataset.optionValue || selectedButton.dataset.label || selectedButton.textContent.trim();
        selectedOptions[index] = optionValue;
        return;
      }
    });
    
    // If we couldn't identify by label, assume Option1 is size and Option2 is color (most common)
    if (!sizeOptionIndex) sizeOptionIndex = 'option1';
    if (!colorOptionIndex) colorOptionIndex = 'option2';
    
    selectedOptions._sizeIndex = sizeOptionIndex;
    selectedOptions._colorIndex = colorOptionIndex;
    
    debugLog('📊 Current selected options:', selectedOptions);
    return selectedOptions;
  }

  /**
   * Find first available color for a given size
   */
  function findFirstAvailableColor(sizeValue, variants, sizeOptionIndex, colorOptionIndex) {
    if (!variants || !sizeValue) {
      debugLog('⚠️ Invalid parameters for findFirstAvailableColor');
      return null;
    }

    debugLog('🔍 Looking for available colors for size:', sizeValue);

    // Determine which variant property corresponds to size and color
    // Default: option1 = size, option2 = color
    const sizeProp = sizeOptionIndex === 'option1' ? 'option1' : 
                     sizeOptionIndex === 'option2' ? 'option2' : 
                     sizeOptionIndex === 'option3' ? 'option3' : 'option1';
    
    const colorProp = colorOptionIndex === 'option1' ? 'option1' : 
                      colorOptionIndex === 'option2' ? 'option2' : 
                      colorOptionIndex === 'option3' ? 'option3' : 'option2';

    // Find all variants that match the selected size
    const matchingVariants = variants.filter(variant => {
      const variantSize = variant[sizeProp];
      return variantSize === sizeValue && variant.available === true;
    });

    debugLog('📦 Found matching available variants:', matchingVariants.length);

    if (matchingVariants.length === 0) {
      debugLog('❌ No available variants found for size:', sizeValue);
      return null;
    }

    // Get the first available color from matching variants
    const firstVariant = matchingVariants[0];
    const colorValue = firstVariant[colorProp];

    debugLog('✅ First available color:', colorValue);
    return colorValue;
  }

  /**
   * Auto-select a color option
   */
  function selectColorOption(colorValue) {
    if (!colorValue) {
      debugLog('⚠️ No color value provided for selection');
      return false;
    }

    debugLog('🎯 Attempting to select color:', colorValue);

    // Find the color option wrapper
    const colorOptionWrapper = findColorOptionWrapper();
    
    if (!colorOptionWrapper) {
      debugLog('❌ Color option wrapper not found');
      return false;
    }

    // Try to find and click the color button/swatch
    let colorButton = null;

    // Method 1: Find by data-option-value attribute
    colorButton = colorOptionWrapper.querySelector(`[dynamic-variant-button][data-option-value="${colorValue}"]`);
    
    // Method 2: Find by aria-label
    if (!colorButton) {
      const allColorButtons = colorOptionWrapper.querySelectorAll('[dynamic-variant-button]');
      colorButton = Array.from(allColorButtons).find(btn => {
        const label = btn.getAttribute('aria-label') || btn.dataset.label || btn.textContent.trim();
        return label === colorValue;
      });
    }

    // Method 3: Find by exact text match
    if (!colorButton) {
      const allColorButtons = colorOptionWrapper.querySelectorAll('[dynamic-variant-button]');
      colorButton = Array.from(allColorButtons).find(btn => {
        return btn.textContent.trim() === colorValue;
      });
    }

    if (!colorButton) {
      debugLog('❌ Color button not found for:', colorValue);
      return false;
    }

    // Check if button is disabled (sold out)
    if (colorButton.classList.contains('disabled') || colorButton.hasAttribute('disabled')) {
      debugLog('⚠️ Color button is disabled:', colorValue);
      return false;
    }

    // Check if already selected
    if (colorButton.classList.contains('selected') || colorButton.getAttribute('aria-selected') === 'true') {
      debugLog('ℹ️ Color already selected:', colorValue);
      return true;
    }

    // Click the button to select it
    debugLog('✅ Clicking color button:', colorValue);
    colorButton.click();

    return true;
  }

  /**
   * Handle size option change
   */
  function handleSizeChange() {
    debugLog('🔄 Size option changed');

    // Get product variants
    const variants = getProductVariants();
    if (!variants) {
      debugLog('⚠️ Cannot proceed without variant data');
      return;
    }

    // Get currently selected options
    const selectedOptions = getSelectedOptions();
    const sizeOptionIndex = selectedOptions._sizeIndex || 'option1';
    const colorOptionIndex = selectedOptions._colorIndex || 'option2';
    const selectedSize = selectedOptions[sizeOptionIndex];

    if (!selectedSize) {
      debugLog('⚠️ No size selected');
      return;
    }

    debugLog('📏 Selected size:', selectedSize, 'at index:', sizeOptionIndex);

    // Find first available color for this size
    const firstAvailableColor = findFirstAvailableColor(selectedSize, variants, sizeOptionIndex, colorOptionIndex);

    if (!firstAvailableColor) {
      debugLog('⚠️ No available color found for size:', selectedSize);
      return;
    }

    // Check if a color is already selected
    const currentColor = selectedOptions[colorOptionIndex];
    
    // Determine which variant properties to use for checking availability
    const sizeProp = sizeOptionIndex === 'option1' ? 'option1' : 
                     sizeOptionIndex === 'option2' ? 'option2' : 
                     sizeOptionIndex === 'option3' ? 'option3' : 'option1';
    
    const colorProp = colorOptionIndex === 'option1' ? 'option1' : 
                      colorOptionIndex === 'option2' ? 'option2' : 
                      colorOptionIndex === 'option3' ? 'option3' : 'option2';
    
    // Only auto-select if no color is selected, or if current color is not available for this size
    if (currentColor) {
      // Check if current color is available for the new size
      const isCurrentColorAvailable = variants.some(variant => 
        variant[sizeProp] === selectedSize && 
        variant[colorProp] === currentColor && 
        variant.available === true
      );

      if (isCurrentColorAvailable) {
        debugLog('ℹ️ Current color is still available, keeping selection:', currentColor);
        return;
      } else {
        debugLog('⚠️ Current color not available for new size, switching...');
      }
    }

    // Auto-select the first available color
    // Use setTimeout to ensure the DOM has updated and variant availability has been recalculated
    // after size selection. The delay allows the theme's variantAvailability function to update
    // the disabled states of color buttons before we try to select one.
    setTimeout(() => {
      const success = selectColorOption(firstAvailableColor);
      if (success) {
        debugLog('✅ Successfully auto-selected color:', firstAvailableColor);
      } else {
        debugLog('❌ Failed to auto-select color:', firstAvailableColor);
        // Retry once more after a longer delay in case DOM wasn't ready
        setTimeout(() => {
          const retrySuccess = selectColorOption(firstAvailableColor);
          if (retrySuccess) {
            debugLog('✅ Successfully auto-selected color on retry:', firstAvailableColor);
          }
        }, 200);
      }
    }, 200);
  }

  /**
   * Find the size option wrapper (usually Option1, but could be different)
   */
  function findSizeOptionWrapper() {
    // Try Option1 first (most common case)
    let sizeOptionWrapper = document.querySelector('[data-product-option="Option1"][dynamic-variant-input-wrap], [data-index="option1"][dynamic-variant-input-wrap]');
    
    if (sizeOptionWrapper) {
      // Check if this is actually a size option by looking at the label
      const label = sizeOptionWrapper.querySelector('label');
      if (label) {
        const labelText = label.textContent.toLowerCase();
        if (labelText.includes('size') || labelText.includes('colour') || labelText.includes('color')) {
          // If it's color, try Option2 instead
          if (labelText.includes('colour') || labelText.includes('color')) {
            sizeOptionWrapper = null;
          }
        }
      }
    }
    
    // If Option1 is not size, try to find the actual size option
    if (!sizeOptionWrapper) {
      const allOptionWrappers = document.querySelectorAll('[dynamic-variant-input-wrap]');
      for (let wrapper of allOptionWrappers) {
        const label = wrapper.querySelector('label');
        if (label) {
          const labelText = label.textContent.toLowerCase();
          if (labelText.includes('size') && !labelText.includes('colour') && !labelText.includes('color')) {
            sizeOptionWrapper = wrapper;
            break;
          }
        }
      }
    }
    
    return sizeOptionWrapper;
  }

  /**
   * Find the color option wrapper (usually Option2, but could be different)
   */
  function findColorOptionWrapper() {
    // Try Option2 first (most common case)
    let colorOptionWrapper = document.querySelector('[data-product-option="Option2"][dynamic-variant-input-wrap], [data-index="option2"][dynamic-variant-input-wrap]');
    
    // If Option2 is not color, try to find the actual color option
    if (!colorOptionWrapper) {
      const allOptionWrappers = document.querySelectorAll('[dynamic-variant-input-wrap]');
      for (let wrapper of allOptionWrappers) {
        const label = wrapper.querySelector('label');
        if (label) {
          const labelText = label.textContent.toLowerCase();
          if (labelText.includes('colour') || labelText.includes('color')) {
            colorOptionWrapper = wrapper;
            break;
          }
        }
      }
    }
    
    return colorOptionWrapper;
  }

  /**
   * Setup event listeners for size option changes
   */
  function setupSizeChangeListener() {
    debugLog('🔧 Setting up size change listener...');

    // Find the size option wrapper
    const sizeOptionWrapper = findSizeOptionWrapper();
    
    if (!sizeOptionWrapper) {
      debugLog('⚠️ Size option wrapper not found, retrying...');
      // Retry after a short delay in case DOM is not ready
      setTimeout(setupSizeChangeListener, 500);
      return;
    }

    debugLog('✅ Found size option wrapper');

    // Listen for changes on select dropdown
    const sizeSelect = sizeOptionWrapper.querySelector('select[dynamic-variant-input]');
    if (sizeSelect) {
      sizeSelect.addEventListener('change', function() {
        debugLog('📝 Size select changed:', this.value);
        handleSizeChange();
      });
      debugLog('✅ Added listener to size select');
    }

    // Listen for clicks on size buttons (chips/swatches)
    const sizeButtons = sizeOptionWrapper.querySelectorAll('[dynamic-variant-button]');
    if (sizeButtons.length > 0) {
      sizeButtons.forEach(button => {
        button.addEventListener('click', function() {
          // Use setTimeout to ensure the click event has been processed and variant availability updated
          setTimeout(() => {
            debugLog('🖱️ Size button clicked:', this.dataset.optionValue || this.textContent.trim());
            handleSizeChange();
          }, 150);
        });
      });
      debugLog('✅ Added listeners to size buttons:', sizeButtons.length);
    }

    // Also listen for the custom variant change event
    document.addEventListener('product:variant-change', function(event) {
      debugLog('📢 Variant change event received');
      // Check if it was a size change
      const selectedOptions = getSelectedOptions();
      if (selectedOptions.option1) {
        handleSizeChange();
      }
    });

    debugLog('✅ Size change listener setup complete');
  }

  /**
   * Initialize the auto-switch functionality
   */
  function init() {
    debugLog('🚀 Initializing auto-switch variant color...');

    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', setupSizeChangeListener);
    } else {
      // DOM is already ready
      setupSizeChangeListener();
    }
  }

  // Start initialization
  init();

})();

